import * as React from "react";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { usePdfFromHtml } from "@/hooks/usePdfFromHtml";
import { BoletoBarcode } from "./BoletoBarcode";

// Reusing the same type definitions
interface Address {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

interface Party {
  name: string;
  document: string;
  email: string;
  phone: string;
  address: Address;
}

interface InvoiceItem {
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

interface Totals {
  subtotal: number;
  tax: number;
  total: number;
}

interface PaymentInfo {
  method: string;
  dueDate: string;
  instructions: string;
  boletoBarcode?: string;
  boletoDigitableLine?: string;
}

interface InvoiceMeta {
  number: string;
  issueDate: string;
  dueDate: string;
  currency: string;
}

interface InvoiceData {
  meta: InvoiceMeta;
  issuer: Party;
  customer: Party;
  items: InvoiceItem[];
  totals: Totals;
  paymentInfo: PaymentInfo;
  notes: string;
  pixQrCodeBase64?: string;
  boletoUrl?: string;
  payment?: {
    pixPayload?: string;
    pixKey?: string;
    boletoLine?: string;
    boletoBarcode?: string;
  };
}

interface InvoiceProps {
  data?: Partial<InvoiceData>;
  logoUrl?: string;
}

// Utility functions (reused)
const fmtBRL = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

const fmtDate = (dateStr: string): string => {
  if (!dateStr) return '';
  // Handle both date objects and strings
  const date = typeof dateStr === 'string' && dateStr.includes('/') 
    ? dateStr 
    : new Date(dateStr).toLocaleDateString('pt-BR');
  return date;
};

const ensureAddress = (addr?: Partial<Address>): Address => ({
  street: addr?.street || '',
  city: addr?.city || '',
  state: addr?.state || '',
  zipCode: addr?.zipCode || '',
  country: addr?.country || 'Brasil'
});

const ensureParty = (party?: Partial<Party>): Party => ({
  name: party?.name || '',
  document: party?.document || '',
  email: party?.email || '',
  phone: party?.phone || '',
  address: ensureAddress(party?.address)
});

const addrLine = (a: Address): string => {
  const parts: string[] = [];
  
  if (a.street) {
    parts.push(a.street);
  }
  
  if (a.city && a.state) {
    const stateAbbr = a.state.substring(0, 2).toUpperCase();
    parts.push(`${a.city} - ${stateAbbr}`);
  }
  
  return parts.join(" - ");
};

const normalizeInvoiceData = (input?: Partial<InvoiceData>): InvoiceData => {
  const meta = {
    number: input?.meta?.number || 'INV-001',
    issueDate: input?.meta?.issueDate || new Date().toISOString(),
    dueDate: input?.meta?.dueDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    currency: input?.meta?.currency || 'BRL'
  };

  const issuer = ensureParty(input?.issuer);
  const customer = ensureParty(input?.customer);
  const items = input?.items || [];
  
  const subtotal = items.reduce((sum, item) => sum + (item.total || 0), 0);
  const tax = 0; // Impostos já inclusos no preço do plano
  const total = subtotal; // Total = subtotal sem adicionar impostos

  const totals = {
    subtotal,
    tax,
    total
  };

  const paymentInfo = {
    method: input?.paymentInfo?.method || 'PIX',
    dueDate: input?.paymentInfo?.dueDate || meta.dueDate,
    instructions: input?.paymentInfo?.instructions || 'Pagamento via PIX',
    boletoBarcode: input?.paymentInfo?.boletoBarcode || '',
    boletoDigitableLine: input?.paymentInfo?.boletoDigitableLine || ''
  };

  return {
    meta,
    issuer,
    customer,
    items,
    totals,
    paymentInfo,
    notes: input?.notes || '',
    payment: input?.payment,
    pixQrCodeBase64: input?.pixQrCodeBase64,
    boletoUrl: input?.boletoUrl,
  };
};

export default function InvoiceAWS({ data, logoUrl }: InvoiceProps) {
  const invoice = normalizeInvoiceData(data);
  const { generatePdfFromElement } = usePdfFromHtml();

  const handleDownloadPdf = React.useCallback(async () => {
    await generatePdfFromElement('invoice-a4', {
      filename: `invoice-${invoice.meta.number || 'aws'}.pdf`,
      mode: 'fit-a4',
      paddingPx: 0,
      background: '#ffffff',
    });
  }, [generatePdfFromElement, invoice.meta.number]);


  return (
    <div className="min-h-screen bg-gray-50 p-4">
      {/* Botão Download PDF */}
      <div className="no-print mb-3 flex justify-end">
        <Button onClick={handleDownloadPdf} className="flex items-center gap-2">
          <Download className="h-4 w-4" />
          Baixar PDF
        </Button>
      </div>

      {/* Invoice Container */}
      <div id="invoice-a4" className="a4-page a4-shadow">
        <div className="a4-content">
          {/* AWS-style Header */}
          <div className="bg-slate-800 text-white -mt-[45px] -mx-[45px] mb-4">
            <div className="p-4">
            <div className="flex justify-between items-start gap-2">
              <div className="flex-1 min-w-0">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" className="h-8 mb-3 filter brightness-0 invert flex-shrink-0" />
                ) : (
                  <div className="text-lg font-bold mb-3 text-orange-400 flex-shrink-0">PortalONE Suite</div>
                )}
                <h1 className="text-2xl font-bold text-white whitespace-nowrap">FATURA</h1>
                <div className="flex items-center mt-2">
                  <span className="bg-orange-500 text-black font-bold px-3 py-1 text-sm break-words">
                    #{invoice.meta.number}
                  </span>
                </div>
              </div>
              <div className="text-right flex-shrink-0 max-w-[250px]">
                <div className="bg-slate-700 p-4 rounded">
                  <table className="text-sm">
                    <tbody>
                      <tr>
                        <td className="text-gray-300 pr-4 whitespace-nowrap">Data de Emissão:</td>
                        <td className="text-white font-mono whitespace-nowrap">{fmtDate(invoice.meta.issueDate)}</td>
                      </tr>
                      <tr>
                        <td className="text-gray-300 pr-4 whitespace-nowrap">Vencimento:</td>
                        <td className="text-white font-mono">{fmtDate(invoice.meta.dueDate)}</td>
                      </tr>
                      <tr>
                        <td className="text-gray-300 pr-4">Moeda:</td>
                        <td className="text-white font-mono">{invoice.meta.currency}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div className="h-1 bg-gradient-to-r from-orange-500 to-orange-400"></div>
        </div>

          {/* Content Area */}
          <div>
          {/* Parties Information */}
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            {/* Billed From */}
            <div>
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-4 border-b border-orange-200 pb-2">
                Emitente
              </h3>
              <div className="space-y-1 text-gray-800">
                <p className="font-bold text-lg">{invoice.issuer.name}</p>
                <p className="text-sm font-mono text-gray-600">CNPJ: {invoice.issuer.document}</p>
                <p className="text-sm">{invoice.issuer.email}</p>
                <p className="text-sm">{invoice.issuer.phone}</p>
                <div className="text-sm text-gray-700">{addrLine(invoice.issuer.address)}</div>
              </div>
            </div>

            {/* Billed To */}
            <div>
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-4 border-b border-gray-200 pb-2">
                Cliente
              </h3>
              <div className="space-y-1 text-gray-800">
                <p className="font-bold text-lg">{invoice.customer.name}</p>
                <p className="text-sm font-mono text-gray-600">CNPJ: {invoice.customer.document}</p>
                <p className="text-sm">{invoice.customer.email}</p>
                <p className="text-sm">{invoice.customer.phone}</p>
                <div className="text-sm text-gray-700">{addrLine(invoice.customer.address)}</div>
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="mb-4">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 border-b border-orange-200 pb-1">
              Detalhes da Fatura
            </h3>
            <div className="border border-gray-300">
              <table className="a4-table">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left text-xs font-bold text-gray-700 uppercase tracking-wider col-desc">
                      Descrição
                    </th>
                    <th className="text-center text-xs font-bold text-gray-700 uppercase tracking-wider col-qtd">
                      Qtd
                    </th>
                    <th className="text-right text-xs font-bold text-gray-700 uppercase tracking-wider col-unit">
                      Valor Unit.
                    </th>
                    <th className="text-right text-xs font-bold text-gray-700 uppercase tracking-wider col-total">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {invoice.items.map((item, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="text-gray-800 font-medium text-xs">{item.description}</td>
                      <td className="text-center font-mono text-xs text-gray-800">{item.quantity}</td>
                      <td className="text-right font-mono text-xs text-gray-800">{fmtBRL(item.unitPrice)}</td>
                      <td className="text-right font-mono font-semibold text-xs text-gray-800">{fmtBRL(item.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Summary Section - Alinhado à direita */}
          <div className="flex justify-end mb-4">
            <div className="w-80">
              <table className="w-full text-sm">
                <tbody>
                  <tr className="border-b">
                    <td className="py-2 text-gray-700">Subtotal:</td>
                    <td className="py-2 text-right font-mono text-gray-800">{fmtBRL(invoice.totals.subtotal)}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 text-gray-700">Impostos:</td>
                    <td className="py-2 text-right font-mono text-gray-800">{fmtBRL(invoice.totals.tax)}</td>
                  </tr>
                  <tr className="border-b-2 border-gray-400 bg-gray-50">
                    <td className="py-3 font-bold text-gray-800">Total:</td>
                    <td className="py-3 text-right text-xl font-bold text-orange-600 font-mono">
                      {fmtBRL(invoice.totals.total)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Payment Information - Seção separada */}
          <div className="mb-4">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2 border-b border-orange-200 pb-1">
              Informações de Pagamento
            </h3>
            <div className="bg-orange-50 border-l-4 border-orange-400 p-2">
              <p className="text-sm text-gray-700">{invoice.paymentInfo.instructions}</p>

              {invoice.payment?.pixPayload && (
                <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* QR Code - Maior */}
                  {invoice.pixQrCodeBase64 ? (
                    <div className="flex justify-center">
                      <img 
                        src={invoice.pixQrCodeBase64.startsWith('data:image') ? invoice.pixQrCodeBase64 : `data:image/png;base64,${invoice.pixQrCodeBase64}`}
                        alt="QR Code PIX" 
                        className="border rounded-lg p-2 w-48 h-48 object-contain bg-white"
                      />
                    </div>
                  ) : (
                    <div className="border rounded-lg p-2 h-48 grid place-items-center text-xs text-orange-500">
                      QR PIX não disponível
                    </div>
                  )}
                  
                  {/* PIX Copia e Cola */}
                  <div className="md:col-span-2">
                    <div className="font-medium text-sm mb-2">PIX Copia e Cola:</div>
                    <div className="p-2 bg-white rounded border text-xs font-mono break-all max-h-32 overflow-auto text-gray-900">
                      {invoice.payment.pixPayload}
                    </div>
                    {invoice.payment.pixKey && (
                      <div className="mt-3 text-sm">
                        <span className="font-medium">Chave PIX:</span> {invoice.payment.pixKey}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {(invoice.paymentInfo.boletoBarcode || invoice.paymentInfo.boletoDigitableLine) && (
                <div className="mt-4 pt-4 border-t border-orange-200">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    Informações de Pagamento - Boleto
                  </h4>
                  <BoletoBarcode
                    barcode={invoice.paymentInfo.boletoBarcode}
                    digitableLine={invoice.paymentInfo.boletoDigitableLine}
                    boletoUrl={invoice.boletoUrl}
                  />
                </div>
              )}
            </div>
          </div>

          </div>

          {/* Rodapé com Observações */}
          <footer className="mt-4 pt-2 border-t">
          <div className="text-center text-[10px] text-gray-400 space-y-0.5">
            <p>Esta fatura não substitui a emissão de documento fiscal quando obrigatório (NF-e/NFS-e).</p>
            <p>Multa de 2% e juros de 1% a.m. após o vencimento (se aplicável).</p>
            <p className="mt-1">Fatura gerada automaticamente pelo sistema PortalONE. Para dúvidas entre em contato conosco.</p>
            <p className="mt-1">Fatura gerada automaticamente pelo sistema PortalONE. Para dúvidas entre em contato conosco.</p>
          </div>
          </footer>
        </div>
      </div>
    </div>
  );
}